import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import Input from '../../components/ui/Input/Input';
import Spinner from '../../components/ui/Spinner/Spinner';
import { getPetDetailsFromUniversalId, getDerivedPet, evaluatePotentialDuplicates, mergeDuplicatePet } from '../../controllers/pets';
import { getParticipantPriorValues } from '../../controllers/participants';
import { submitExternalConsultation, getExternalChatMessages } from '../../controllers/external';
import Select from '../../components/ui/Select/Select';
import './PetDetailsScreen.css';
import { useToast } from '../../contexts/ToastContext';

const PetDetailsScreen = () => {
  const { pet_id } = useParams();
  const navigate = useNavigate();
  const [pet, setPet] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [keysPerParticipant, setKeysPerParticipant] = useState([]);
  const [petParents, setPetParents] = useState([]);
  const [incubations, setIncubations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [priorValues, setPriorValues] = useState(null);
  const [priorValuesLoading, setPriorValuesLoading] = useState(false);
  const [priorValuesError, setPriorValuesError] = useState(null);
  const [expandedFields, setExpandedFields] = useState(new Set());
  const [petInfoFields, setPetInfoFields] = useState(null);
  const [consultationsByPet, setConsultationsByPet] = useState({});
  const [petDropdownOptions, setPetDropdownOptions] = useState([]);
  const [selectedPetName, setSelectedPetName] = useState('');
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [potentialDuplicates, setPotentialDuplicates] = useState([]);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [mergeIncubationId, setMergeIncubationId] = useState('');
  const [mergeConfirm, setMergeConfirm] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const { showSuccess, showError } = useToast();

  const formatDisplayName = (name) => {
    if (!name || typeof name !== 'string') return '';
    return name
      .replace(/[_-]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  };

  const formatEngagementText = (text) => {
    if (!text || typeof text !== 'string') return 'N/A';
    return text
      .replace(/[_-]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  };

  const formatConsultationType = (type) => {
    if (!type || typeof type !== 'string') return 'N/A';
    const upper = type.toUpperCase();
    if (upper === 'CHAT' || upper === 'THREAD') return 'Chat';
    if (upper === 'VIDEO') return 'Video';
    return formatEngagementText(type);
  };

  const formatValue = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return String(value);
  };

  const renderPreviousValuesTable = (fieldKey) => {
    const entries = priorValues?.[fieldKey] || [];
    if (!Array.isArray(entries) || entries.length === 0) return null;
    const sortedEntries = [...entries].sort((a, b) => new Date(b.date) - new Date(a.date));
    return (
      <table className="prior-values-table">
        <tbody>
          {sortedEntries.map((entry, index) => (
            <tr key={index}>
              <td className="prior-values-date">{new Date(entry.date).toLocaleString()}</td>
              <td className="prior-values-value">{formatValue(entry.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  useEffect(() => {
    const fetchPetData = async () => {
      if (!pet_id) {
        setError('No pet ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Use the new getDerivedPet API call
        const response = await getDerivedPet(pet_id);
        
        if (response.data && response.data.success) {
          setPet(response.data.pet);
          setParticipants(response.data.participants || []);
          setKeysPerParticipant(response.data.keys_per_participant || []);
          setPetParents(response.data.pet_parents || []);
          setIncubations(response.data.incubations || {});

          // Build pet info fields and histories from derived data payload
          const derivedContainer = response.data?.data || response.data?.derived_data || null;
          if (derivedContainer && typeof derivedContainer === 'object') {
            const histories = {};
            const flattened = {};

            // If nested derived_data exists (e.g., "data.derived_data"), include those
            if (derivedContainer.derived_data && typeof derivedContainer.derived_data === 'object') {
              Object.entries(derivedContainer.derived_data).forEach(([key, entries]) => {
                if (Array.isArray(entries) && entries.length > 0) {
                  histories[key] = entries;
                  const latest = [...entries].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
                  flattened[key] = latest ? latest.value : null;
                }
              });
            }

            // Include other keys in container (skip the nested derived_data key to avoid duplicates)
            Object.entries(derivedContainer).forEach(([key, entries]) => {
              if (key === 'derived_data') return;
              if (Array.isArray(entries) && entries.length > 0) {
                histories[key] = entries;
                const latest = [...entries].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
                flattened[key] = latest ? latest.value : null;
              }
            });

            setPetInfoFields(Object.keys(flattened).length > 0 ? flattened : null);
            // Reuse priorValues UI with the full histories for expansion
            if (Object.keys(histories).length > 0) {
              setPriorValues(histories);
            }
          }
        } else {
          setError(response.data?.error || 'Failed to fetch pet data');
        }
      } catch (err) {
        // Handle authentication errors by redirecting to login
        if (err.message.includes('Authentication required')) {
          // Clear any stored token and redirect to login
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        
        setError(err.message || 'An error occurred while fetching pet data');
        console.error('Error fetching pet:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPetData();
  }, [pet_id, navigate]);

  // Evaluate potential duplicates for this pet and populate section
  useEffect(() => {
    if (!pet_id) return;
    (async () => {
      try {
        const res = await evaluatePotentialDuplicates(pet_id);
        const dupeArray =
          (res && res.data && res.data.potential_duplicates) ||
          (res && res.data && res.data.data && res.data.data.potential_duplicates) ||
          [];
        if (Array.isArray(dupeArray)) setPotentialDuplicates(dupeArray);
        console.log('evaluatePotentialDuplicates result:', res?.data);
      } catch (err) {
        console.error('evaluatePotentialDuplicates failed:', err);
        setPotentialDuplicates([]);
      }
    })();
  }, [pet_id]);

  // Fetch external consultations for associated parent emails, log responses, and aggregate by pet name
  useEffect(() => {
    if (!petParents || petParents.length === 0) return;

    const uniqueEmails = Array.from(new Set(
      petParents
        .map(p => (p && typeof p.email === 'string' ? p.email.trim() : ''))
        .filter(email => email.length > 0)
    ));

    if (uniqueEmails.length === 0) return;

    const fetchConsultationsForParents = async () => {
      const partnerName = 'hills';
      const results = await Promise.allSettled(
        uniqueEmails.map(async (email) => {
          try {
            const response = await submitExternalConsultation({ email, partner_name: partnerName });
            
            const consultations =
              (response && response.data && response.data.care_consultations) ||
              (response && response.data && response.data.data && response.data.data.care_consultations) ||
              {};
            return consultations;
          } catch (err) {
            console.error('Failed to fetch external consultations for email:', email, err);
            return {};
          }
        })
      );

      // Merge consultations from all email responses into a single map keyed by pet name
      const merged = {};
      results.forEach((r) => {
        if (r.status === 'fulfilled' && r.value && typeof r.value === 'object') {
          Object.entries(r.value).forEach(([petName, items]) => {
            if (!petName) return;
            const list = Array.isArray(items) ? items : [];
            if (!merged[petName]) merged[petName] = [];
            merged[petName].push(...list);
          });
        }
      });

      // Deduplicate within each pet by a stable id
      Object.keys(merged).forEach((name) => {
        const seen = new Set();
        merged[name] = merged[name].filter((item) => {
          const key = item?.id || item?._id || item?.e_id || JSON.stringify(item);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      });

      // Merge with any existing state
      setConsultationsByPet((prev) => {
        const combined = { ...prev };
        Object.entries(merged).forEach(([petName, newItems]) => {
          if (!combined[petName]) combined[petName] = [];
          const seen = new Set(
            combined[petName].map((i) => i?.id || i?._id || i?.e_id || JSON.stringify(i))
          );
          newItems.forEach((i) => {
            const key = i?.id || i?._id || i?.e_id || JSON.stringify(i);
            if (!seen.has(key)) {
              combined[petName].push(i);
              seen.add(key);
            }
          });
        });
        return combined;
      });
    };

    fetchConsultationsForParents();
  }, [petParents]);

  // Build dropdown options from aggregated consultations and auto-select current pet name
  useEffect(() => {
    const allNames = Object.keys(consultationsByPet).filter(
      (n) => n && n.toLowerCase() !== 'unassigned'
    );
    setPetDropdownOptions(allNames.map((n) => ({ value: n, label: formatDisplayName(n) })));

    const currentName = (petInfoFields && petInfoFields.name) || (pet && pet.name) || '';
    const currentLower = currentName ? String(currentName).toLowerCase() : '';

    if (!selectedPetName || !allNames.includes(selectedPetName)) {
      const match = allNames.find((n) => n.toLowerCase() === currentLower);
      if (match) {
        setSelectedPetName(match);
      } else if (allNames.length > 0) {
        setSelectedPetName(allNames[0]);
      } else {
        setSelectedPetName('');
      }
    }
  }, [consultationsByPet, petInfoFields, pet]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleConnectIncubationClick = () => {
    setIsMergeModalOpen(true);
    setMergeIncubationId('');
    setMergeConfirm(false);
  };

  const closeMergeModal = () => {
    setIsMergeModalOpen(false);
    setMergeIncubationId('');
    setMergeConfirm(false);
  };

  const handleMergeClick = () => {
    if (!mergeConfirm) {
      setMergeConfirm(true);
      return;
    }
    // Final confirm action placeholder
    (async () => {
      const trimmed = (mergeIncubationId || '').trim();
      if (!trimmed) {
        showError('Please enter a valid Pet Incubation ID.');
        return;
      }
      try {
        if (isMerging) return;
        setIsMerging(true);
        const res = await mergeDuplicatePet({ pet_incubation_id: trimmed, universal_pet_id: pet_id });
        if (res && res.data && res.data.success) {
          showSuccess('Merge successful');
          // Refresh derived pet data to update incubations card
          try {
            const refreshed = await getDerivedPet(pet_id);
            if (refreshed?.data?.success) {
              setPet(refreshed.data.pet);
              setParticipants(refreshed.data.participants || []);
              setKeysPerParticipant(refreshed.data.keys_per_participant || []);
              setPetParents(refreshed.data.pet_parents || []);
              setIncubations(refreshed.data.incubations || {});

              const derivedContainer = refreshed.data?.data || refreshed.data?.derived_data || null;
              if (derivedContainer && typeof derivedContainer === 'object') {
                const histories = {};
                const flattened = {};
                if (derivedContainer.derived_data && typeof derivedContainer.derived_data === 'object') {
                  Object.entries(derivedContainer.derived_data).forEach(([key, entries]) => {
                    if (Array.isArray(entries) && entries.length > 0) {
                      histories[key] = entries;
                      const latest = [...entries].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
                      flattened[key] = latest ? latest.value : null;
                    }
                  });
                }
                Object.entries(derivedContainer).forEach(([key, entries]) => {
                  if (key === 'derived_data') return;
                  if (Array.isArray(entries) && entries.length > 0) {
                    histories[key] = entries;
                    const latest = [...entries].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
                    flattened[key] = latest ? latest.value : null;
                  }
                });
                setPetInfoFields(Object.keys(flattened).length > 0 ? flattened : null);
                if (Object.keys(histories).length > 0) {
                  setPriorValues(histories);
                }
              }
            }
            // Also refresh possible duplicates after merge
            try {
              const dupeRes = await evaluatePotentialDuplicates(pet_id);
              const dupeArray =
                (dupeRes && dupeRes.data && dupeRes.data.potential_duplicates) ||
                (dupeRes && dupeRes.data && dupeRes.data.data && dupeRes.data.data.potential_duplicates) ||
                [];
              setPotentialDuplicates(Array.isArray(dupeArray) ? dupeArray : []);
            } catch {}
          } catch {}
          // Close modal after success
          closeMergeModal();
        } else {
          showError(res?.data?.error || 'Merge failed');
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem('token');
          showError('Authentication required. Please log in again.');
          navigate('/login');
          return;
        }
        showError(err?.response?.data?.error || err?.message || 'Unexpected error during merge');
        console.error('Merge error:', err);
      } finally {
        setIsMerging(false);
      }
    })();
  };

  const handleConsultationRowClick = async (consultation) => {
    setSelectedConsultation(consultation || null);
    setIsConsultationModalOpen(true);
    setChatMessages([]);
    setChatError(null);
    setChatLoading(false);

    const typeUpper = String(consultation?.type || '').toUpperCase();
    if (typeUpper === 'CHAT' || typeUpper === 'THREAD') {
      try {
        setChatLoading(true);
        const careConsultationId = consultation?.id || consultation?._id || consultation?.e_id || consultation?.code;
        if (!careConsultationId) {
          setChatError('Missing consultation id for chat retrieval');
          setChatLoading(false);
          return;
        }
        const response = await getExternalChatMessages({ care_consultation_id: careConsultationId });
        const raw = response?.data;
        const extracted = extractMessagesFromResponse(raw);
        setChatMessages(extracted);
      } catch (err) {
        console.error('Failed to fetch chat messages for consultation:', err);
        setChatError('Failed to fetch chat messages');
      } finally {
        setChatLoading(false);
      }
    }
  };

  const closeConsultationModal = () => {
    setIsConsultationModalOpen(false);
    setSelectedConsultation(null);
    setChatMessages([]);
    setChatError(null);
    setChatLoading(false);
  };

  const handleDuplicateRowClick = (duplicateItem) => {
    setSelectedDuplicate(duplicateItem || null);
    setIsDuplicateModalOpen(true);
  };

  const closeDuplicateModal = () => {
    setIsDuplicateModalOpen(false);
    setSelectedDuplicate(null);
  };

  const extractMessagesFromResponse = (raw) => {
    if (!raw) return [];
    const candidates = Array.isArray(raw)
      ? raw
      : raw.messages || raw.chat_messages || raw.data?.messages || raw.data?.chat_messages || [];
    if (!Array.isArray(candidates)) return [];
    return candidates.map((m, idx) => {
      const typeUpper = String(m?.type || m?.message_type || m?.kind || '').toUpperCase();
      const isImage = typeUpper === 'IMAGE';
      const isVideo = typeUpper === 'VIDEO';
      const displayContent = isImage
        ? 'Image Message'
        : (isVideo ? 'Video Message' : (m?.message || m?.text || m?.content || ''));
      const timestamp = m?.timestamp || m?.created_at || m?.sent_at || m?.updated_at || null;
      const isFromClient =
        m?.is_from_client === true ||
        String(m?.sender || m?.role || '').toLowerCase().includes('client');
      const isFromProvider =
        m?.is_from_provider === true ||
        String(m?.sender || m?.role || '').toLowerCase().includes('provider') ||
        String(m?.sender || m?.role || '').toLowerCase().includes('vet') ||
        String(m?.sender || m?.role || '').toLowerCase().includes('agent');
      const author = isFromClient ? 'Client' : (isFromProvider ? 'Provider' : (m?.sender || m?.role || 'System'));
      return {
        id: m?.id || m?._id || idx,
        author,
        isClient: isFromClient,
        content: displayContent,
        isMediaPlaceholder: isImage || isVideo,
        timestamp,
        messageType: typeUpper
      };
    });
  };

  const handleParentRowClick = (email) => {
    navigate('/parent-details', { state: { email } });
  };

  const fetchPriorValues = async () => {
    if (!pet_id) return;

    try {
      setPriorValuesLoading(true);
      setPriorValuesError(null);
      const response = await getParticipantPriorValues(pet_id);
      
      if (response.data && response.data.success) {
        setPriorValues(response.data.prior_values);
      } else {
        setPriorValuesError(response.data?.error || 'Failed to fetch prior values');
      }
    } catch (err) {
      setPriorValuesError('An error occurred while fetching prior values');
      console.error('Error fetching prior values:', err);
    } finally {
      setPriorValuesLoading(false);
    }
  };

  const handleFieldClick = async (fieldName) => {
    const newExpandedFields = new Set(expandedFields);
    
    if (expandedFields.has(fieldName)) {
      // Collapse the field
      newExpandedFields.delete(fieldName);
    } else {
      // Expand the field
      newExpandedFields.add(fieldName);
      
      // Fetch prior values if not already loaded
      if (!priorValues && !priorValuesLoading) {
        await fetchPriorValues();
      }
    }
    
    setExpandedFields(newExpandedFields);
  };

  if (loading) {
    return (
      <div className="pet-details-screen">
        <div className="pet-details-header-container">
          <div className="pet-details-header-left">
            <button className="pet-details-back-button" onClick={handleBackClick}>
              <i className="fas fa-arrow-left"></i>
              <span>Back</span>
            </button>
            <div className="pet-details-header">
              <h1 className="pet-details-title">Pet Details</h1>
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spinner full size="lg" label="Loading pet details..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pet-details-screen">
        <div className="pet-details-header-container">
          <div className="pet-details-header-left">
            <button className="pet-details-back-button" onClick={handleBackClick}>
              <i className="fas fa-arrow-left"></i>
              <span>Back</span>
            </button>
            <div className="pet-details-header">
              <h1 className="pet-details-title">Pet Details</h1>
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '16px' }}></i>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pet-details-screen">
      <div className="pet-details-header-container">
        <div className="pet-details-header-left">
          <button className="pet-details-back-button" onClick={handleBackClick}>
            <i className="fas fa-arrow-left"></i>
            <span>Back</span>
          </button>
          <div className="pet-details-header">
            <h1 className="pet-details-title">Pet Details</h1>
          </div>
        </div>
      </div>
      
      <div className="pet-details-cards-container">
        <div className="pet-details-left-column">
          <Card className="chart-section pet-details-card">
            <div className="card-header">
              <h2 className="chart-title">Pet Information</h2>
            </div>
            <div className="pet-details-card-content">
              {petInfoFields ? (
                <div className="pet-data-grid">
                  {Object.entries(petInfoFields).map(([key, value]) => (
                    <div key={key} className="pet-data-item">
                      <div className="pet-data-row">
                        <div className="pet-data-field-header" onClick={() => handleFieldClick(key)}>
                          <strong className="clickable-field-label">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                          </strong>
                        </div>
                        <span className="pet-data-value">{value !== null && value !== undefined ? formatValue(value) : 'N/A'}</span>
                      </div>

                      {expandedFields.has(key) && priorValues && priorValues[key] && (
                        renderPreviousValuesTable(key)
                      )}

                      {expandedFields.has(key) && priorValuesLoading && (
                        <div className="prior-values-loading">
                          <i className="fas fa-spinner fa-spin"></i>
                          <span>Loading history...</span>
                        </div>
                      )}

                      {expandedFields.has(key) && priorValues && !priorValues[key] && !priorValuesLoading && (
                        <div className="prior-values-empty">
                          <span>No previous values available for this field.</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : pet && pet.data ? (
                <div className="pet-data-grid">
                  {Object.entries(pet.data).map(([key, value]) => (
                    <div key={key} className="pet-data-item">
                      <div className="pet-data-row">
                        <div className="pet-data-field-header" onClick={() => handleFieldClick(key)}>
                          <strong className="clickable-field-label">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                          </strong>
                        </div>
                        <span className="pet-data-value">{value !== null && value !== undefined ? formatValue(value) : 'N/A'}</span>
                      </div>
                      
                      {expandedFields.has(key) && priorValues && priorValues[key] && (
                        renderPreviousValuesTable(key)
                      )}
                      
                      {expandedFields.has(key) && priorValuesLoading && (
                        <div className="prior-values-loading">
                          <i className="fas fa-spinner fa-spin"></i>
                          <span>Loading history...</span>
                        </div>
                      )}
                      
                      {expandedFields.has(key) && priorValues && !priorValues[key] && !priorValuesLoading && (
                        <div className="prior-values-empty">
                          <span>No previous values available for this field.</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : pet ? (
                <>
                  <div className="pet-data-item">
                    <div className="pet-data-row">
                      <div className="pet-data-field-header" onClick={() => handleFieldClick('name')}>
                        <strong className="clickable-field-label">Pet Name:</strong>
                      </div>
                      <span className="pet-data-value">{pet.name || 'N/A'}</span>
                    </div>
                    
                    {expandedFields.has('name') && priorValues && priorValues.name && (
                      renderPreviousValuesTable('name')
                    )}
                  </div>
                  
                  <div className="pet-data-item">
                    <div className="pet-data-row">
                      <div className="pet-data-field-header" onClick={() => handleFieldClick('universal_id')}>
                        <strong className="clickable-field-label">Universal ID:</strong>
                      </div>
                      <span className="pet-data-value">{pet.universal_id || 'N/A'}</span>
                    </div>
                    
                    {expandedFields.has('universal_id') && priorValues && priorValues.universal_id && (
                      renderPreviousValuesTable('universal_id')
                    )}
                  </div>
                  
                  <div className="pet-data-item">
                    <div className="pet-data-row">
                      <div className="pet-data-field-header" onClick={() => handleFieldClick('teletails_id')}>
                        <strong className="clickable-field-label">Teletails ID:</strong>
                      </div>
                      <span className="pet-data-value">{pet.teletails_id || 'N/A'}</span>
                    </div>
                    
                    {expandedFields.has('teletails_id') && priorValues && priorValues.teletails_id && (
                      renderPreviousValuesTable('teletails_id')
                    )}
                  </div>
                  
                  <div className="pet-data-item">
                    <div className="pet-data-row">
                      <div className="pet-data-field-header" onClick={() => handleFieldClick('created_at')}>
                        <strong className="clickable-field-label">Created:</strong>
                      </div>
                      <span className="pet-data-value">{pet.created_at ? new Date(pet.created_at).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    
                    {expandedFields.has('created_at') && priorValues && priorValues.created_at && (
                      renderPreviousValuesTable('created_at')
                    )}
                  </div>
                </>
              ) : (
                <p>No pet data available</p>
              )}
            </div>
          </Card>
          
          <Card className="chart-section engagements-card">
            <div className="card-header">
              <h2 className="chart-title">Engagements</h2>
              <div className="card-header-buttons">
                <Select
                  options={petDropdownOptions}
                  value={selectedPetName}
                  onChange={(e) => setSelectedPetName(e.target.value)}
                  placeholder="Select Pet"
                />
              </div>
            </div>
            <div className="engagements-card-content">
              {selectedPetName ? (
                (consultationsByPet[selectedPetName] && consultationsByPet[selectedPetName].length > 0) ? (
                  <table className="version-table">
                    <thead>
                      <tr>
                        <th>Engagement</th>
                        <th>Type</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consultationsByPet[selectedPetName]
                        .filter((c) => (c?.status || '').toUpperCase() !== 'CANCELED')
                        .map((c, index) => {
                        const engagement = formatEngagementText(c?.subject || c?.category || c?.status || 'N/A');
                        const type = formatConsultationType(c?.type || '');
                        const dateRaw = c?.updated_at || c?.created_at || c?.last_message_sent_at;
                        const date = dateRaw ? new Date(dateRaw).toLocaleDateString() : 'N/A';
                        const key = c?.id || c?._id || c?.e_id || index;
                        return (
                          <tr key={key} onClick={() => handleConsultationRowClick(c)}>
                            <td className="version-change">{engagement}</td>
                            <td className="version-change">{type}</td>
                            <td className="version-date">{date}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="timeline-empty">
                    <h3>No Consultations</h3>
                    <p>No consultations found for this pet.</p>
                  </div>
                )
              ) : (
                <div className="timeline-empty">
                  <h3>Select a Pet</h3>
                  <p>Choose a pet from the dropdown to view consultations.</p>
                </div>
              )}
            </div>
          </Card>
        </div>
        
        <div className="pet-details-right-column">
          <Card className="chart-section associated-parents-card">
            <div className="card-header">
              <h2 className="chart-title">Associated Parents</h2>
            </div>
            <div className="associated-parents-card-content">
              {petParents && petParents.length > 0 ? (
                <table className="version-table">
                  <tbody>
                    {petParents.map((parent, index) => {
                      const fullName = `${parent.first_name || ''}${parent.first_name || parent.last_name ? ' ' : ''}${parent.last_name || ''}`.trim() || 'N/A';
                      return (
                        <tr key={index}>
                          <td>{fullName}</td>
                          <td>{parent.email || 'N/A'}</td>
                          <td>{parent.phone_number || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="timeline-empty">
                  <h3>No Associated Parents</h3>
                  <p>No parents returned for this pet.</p>
                </div>
              )}
            </div>
          </Card>
          
          <Card className="chart-section incubations-card">
            <div className="card-header">
              <h2 className="chart-title">Incubations</h2>
              <div className="card-header-buttons">
                <Button variant="primary" size="small" onClick={handleConnectIncubationClick}>
                  <i className="fas fa-link"></i>
                  Merge Duplicate Pet
                </Button>
              </div>
            </div>
            <div className="incubations-card-content">
              {incubations && Object.keys(incubations).length > 0 ? (
                <ul className="incubations-list">
                  {Object.entries(incubations).map(([key, name]) => (
                    <li key={key} className="incubation-item">
                      <div className="incubation-info">
                        <div className="incubation-name">
                          {name}
                        </div>
                        <div className="incubation-key">
                          {key}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="incubations-list">
                  <li className="incubation-item">No incubations found</li>
                </ul>
              )}
            </div>
          </Card>
          {/* Potential Duplicates Section (below Incubations) */}
          {potentialDuplicates && potentialDuplicates.length > 0 && (
            <Card className="chart-section incubations-card possible-duplicates-card">
              <div className="card-header">
                <h2 className="chart-title possible-duplicates-title">Possible Duplicates</h2>
              </div>
              <div className="incubations-card-content">
                <table className="version-table">
                  <thead>
                    <tr>
                      <th>Incubation</th>
                      <th>Pet ID</th>
                      <th>Pet Incubation ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {potentialDuplicates.map((item, index) => {
                      const incubationName = (item && item.incubation_name) || (item && item.pet_incubation_id && incubations && incubations[item.pet_incubation_id]) || 'N/A';
                      const key = item?.pet_incubation_id || item?.pet_id || index;
                      const displayPetIncubationId = item?.pet_incubation_id ? String(item.pet_incubation_id).toLowerCase() : 'N/A';
                      return (
                        <tr key={key} onClick={() => handleDuplicateRowClick(item)} style={{ cursor: 'pointer' }}>
                          <td className="version-change">{incubationName}</td>
                          <td className="version-change">{item?.pet_id || 'N/A'}</td>
                          <td className="version-date">{displayPetIncubationId}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          
        </div>
      </div>
      {isConsultationModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={closeConsultationModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Consultation Details</h3>
              <button className="modal-close-button" onClick={closeConsultationModal} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="pet-data-grid">
                <div className="pet-data-item">
                  <div className="pet-data-row">
                    <div className="pet-data-field-header">
                      <strong className="clickable-field-label">Pet Name:</strong>
                    </div>
                    <span className="pet-data-value">{formatDisplayName(selectedConsultation?.patient?.name || 'N/A')}</span>
                  </div>
                </div>
                <div className="pet-data-item">
                  <div className="pet-data-row">
                    <div className="pet-data-field-header">
                      <strong className="clickable-field-label">Breed:</strong>
                    </div>
                    <span className="pet-data-value">{selectedConsultation?.patient?.breed || 'N/A'}</span>
                  </div>
                </div>
                <div className="pet-data-item">
                  <div className="pet-data-row">
                    <div className="pet-data-field-header">
                      <strong className="clickable-field-label">Category:</strong>
                    </div>
                    <span className="pet-data-value">{formatEngagementText(selectedConsultation?.category || 'N/A')}</span>
                  </div>
                </div>
                <div className="pet-data-item">
                  <div className="pet-data-row">
                    <div className="pet-data-field-header">
                      <strong className="clickable-field-label">Resolved Date:</strong>
                    </div>
                    <span className="pet-data-value">{selectedConsultation?.resolved_at ? new Date(selectedConsultation.resolved_at).toLocaleString() : 'N/A'}</span>
                  </div>
                </div>
              </div>

              {(() => {
                const typeUpper = String(selectedConsultation?.type || '').toUpperCase();
                if (typeUpper !== 'CHAT' && typeUpper !== 'THREAD') return null;
                return (
                  <div className="modal-chat-section">
                    <h4 className="modal-chat-title">Chat Transcript</h4>
                    {chatLoading && (
                      <div className="chat-loading">
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Loading chat messages...</span>
                      </div>
                    )}
                    {chatError && (
                      <div className="chat-error">
                        <i className="fas fa-exclamation-triangle"></i>
                        {chatError}
                      </div>
                    )}
                    {!chatLoading && !chatError && (
                      chatMessages && chatMessages.length > 0 ? (
                        <div className="chat-messages">
                          {chatMessages.map((m) => (
                            <div key={m.id} className={`chat-message ${m.isClient ? 'from-client' : 'from-provider'}`}>
                              <div className="chat-message-meta">
                                <span className="chat-author">{m.author}</span>
                                {m.timestamp && (
                                  <span className="chat-timestamp">{new Date(m.timestamp).toLocaleString()}</span>
                                )}
                              </div>
                              <div className="chat-content">{m.isMediaPlaceholder ? (<em>{m.content}</em>) : (m.content || ' ')}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="chat-empty">No chat messages found.</div>
                      )
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {isMergeModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={closeMergeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Merge Duplicate Pet</h3>
              <button className="modal-close-button" onClick={closeMergeModal} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="merge-form-field">
                <label htmlFor="merge-incubation-id-input" className="merge-input-label">Enter Pet Incubation ID</label>
                <Input
                  id="merge-incubation-id-input"
                  placeholder="Enter Pet Incubation ID"
                  value={mergeIncubationId}
                  onChange={(e) => setMergeIncubationId(e.target.value)}
                />
              </div>
            </div>
            {mergeConfirm && (
              <div className="modal-warning-text">This action cannot be reversed</div>
            )}
            <div className="modal-footer">
              <div className="modal-footer-right">
                <Button variant="outline" size="medium" className="action-button" onClick={closeMergeModal}>Cancel</Button>
                <Button
                  variant="primary"
                  size="medium"
                  className="action-button"
                  onClick={handleMergeClick}
                  loading={isMerging}
                  disabled={isMerging}
                  ripple
                >
                  {mergeConfirm ? 'Confirm Merge' : 'Merge'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isDuplicateModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={closeDuplicateModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Potential Duplicate Details</h3>
              <button className="modal-close-button" onClick={closeDuplicateModal} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="pet-data-grid">
                {/* Surface IDs at the top for quick reference */}
                <div className="pet-data-item">
                  <div className="pet-data-row">
                    <div className="pet-data-field-header">
                      <strong className="clickable-field-label">Pet Id:</strong>
                    </div>
                    <span className="pet-data-value">{selectedDuplicate?.pet_id || 'N/A'}</span>
                  </div>
                </div>
                <div className="pet-data-item">
                  <div className="pet-data-row">
                    <div className="pet-data-field-header">
                      <strong className="clickable-field-label">Pet Incubation Id:</strong>
                    </div>
                    <span className="pet-data-value">{selectedDuplicate?.pet_incubation_id || 'N/A'}</span>
                  </div>
                </div>
                {selectedDuplicate && selectedDuplicate.data && typeof selectedDuplicate.data === 'object' && (
                  Object.entries(selectedDuplicate.data).map(([key, value]) => (
                    <div key={key} className="pet-data-item">
                      <div className="pet-data-row">
                        <div className="pet-data-field-header">
                          <strong className="clickable-field-label">{String(key).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>
                        </div>
                        <span className="pet-data-value">{value !== null && value !== undefined ? formatValue(value) : 'N/A'}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PetDetailsScreen;
